import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import {
  boilingPointAt,
  SOLVENTS,
  vaporPressureAt,
} from "@/lib/science/antoine";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

type Mode = "bpAtP" | "pAtT";

export function BoilingPointCalc() {
  const locale = getLocale();
  const [mode, setMode] = useState<Mode>("bpAtP");
  const [solventId, setSolventId] = useState(SOLVENTS[0].id);
  const [pressure, setPressure] = useState("40");
  const [temp, setTemp] = useState("25");

  const solvent = SOLVENTS.find((s) => s.id === solventId) ?? SOLVENTS[0];

  const out = useMemo(() => {
    if (mode === "bpAtP") {
      const v = boilingPointAt(solvent, Number(pressure));
      return v == null
        ? null
        : { value: v, unit: "°C", label: m.bp_bp_label() };
    }
    const v = vaporPressureAt(solvent, Number(temp));
    return v == null
      ? null
      : { value: v, unit: "mmHg", label: m.bp_vp_label() };
  }, [mode, solvent, pressure, temp]);

  const num = (n: number) =>
    n.toLocaleString(locale, { maximumFractionDigits: 1 });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <ChoiceField
        label={m.bp_mode()}
        value={mode}
        onChange={setMode}
        options={[
          { value: "bpAtP", label: m.bp_mode_bp() },
          { value: "pAtT", label: m.bp_mode_vp() },
        ]}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label
            htmlFor="solvent"
            className="block text-sm text-muted-foreground"
          >
            {m.bp_solvent()}
          </label>
          <select
            id="solvent"
            value={solventId}
            onChange={(e) => setSolventId(e.target.value)}
            className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
          >
            {SOLVENTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        {mode === "bpAtP" ? (
          <NumField
            id="pressure"
            label={m.bp_pressure()}
            value={pressure}
            onChange={setPressure}
            suffix="mmHg"
          />
        ) : (
          <NumField
            id="temp"
            label={m.bp_temp()}
            value={temp}
            onChange={setTemp}
            suffix="°C"
          />
        )}
      </div>

      {out ? (
        <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {out.label}
          </span>
          <span
            data-testid="bp-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {num(out.value)} {out.unit}
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.bp_hint()}</p>
      )}
    </div>
  );
}
